import { useAppSelector } from '../app/state';
import { callAction } from '../app/actions';
import type { FavTargetType } from '../api/me';

/**
 * The favorite star (plan 2.4) — one implementation for every place a thing can be starred:
 * the node popup's header and the doc overlay's bar today, wherever Phase 3 puts its watch
 * toggle beside it tomorrow.
 *
 * Subscribes to exactly one boolean (is THIS target starred), so a click re-renders the two
 * stars that show the target and nothing else. The toggle itself is `toggleFavorite` in
 * `app/actions.ts` — optimistic, with rollback — and this component carries no state of its
 * own: what the snapshot says is what the star shows, which is what makes the optimistic
 * flip and its rollback both just render.
 *
 * Hidden while signed out (nothing to attach a favorite to) and while the pane's first load
 * has not answered — a star that shows "not starred" before the list arrives would flip to
 * "starred" a beat later, and a toggle whose truth is not yet known invites double writes.
 */
export function FavStar({
  targetType,
  targetId,
  id,
  px = 17,
}: {
  targetType: FavTargetType;
  targetId: string;
  /** DOM id, so a spec can address one star among several. */
  id?: string;
  px?: number;
}) {
  const ready = useAppSelector((s) => !!s.currentUser && s.myItems.loaded);
  const on = useAppSelector((s) =>
    s.myItems.favorites.some((f) => f.targetType === targetType && f.targetId === targetId),
  );
  if (!ready) return null;
  return (
    <button
      type="button"
      id={id}
      className={on ? 'favbtn on' : 'favbtn'}
      title={on ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={on ? 'true' : 'false'}
      onClick={(e) => {
        e.stopPropagation();
        callAction('toggleFavorite', targetType, targetId);
      }}
    >
      <svg className="ico" style={{ width: px, height: px }}>
        <use href={on ? '#i-star-fill' : '#i-star'} />
      </svg>
    </button>
  );
}
